/**
 * Floor — a level/area of the property containing Rooms.
 *
 * Floor NAME is the user's label ("Main Level", "Finished Basement");
 * Floor TYPE is structural semantics the future floorplan and estimating
 * key on. Types are fixed application constants (small, semantic set —
 * extending it is a deliberate code change), unlike Room Types which are
 * configurable database records.
 *
 * Exterior/site scope attaches to a Floor of type 'exterior'
 * (spec-approved; no fake "Whole House" rooms). No graphical
 * elevation/coordinate data in Phase 3 (see spec Sections 18A–18I for the
 * future geometry model).
 */
export type FloorType = "basement" | "floor" | "attic" | "garage" | "exterior" | "other";

export const FLOOR_TYPES: ReadonlyArray<{ id: FloorType; displayName: string }> = [
  { id: "floor", displayName: "Floor" },
  { id: "basement", displayName: "Basement" },
  { id: "attic", displayName: "Attic" },
  { id: "garage", displayName: "Garage" },
  { id: "exterior", displayName: "Exterior / Site" },
  { id: "other", displayName: "Other" },
];

export function floorTypeDisplayName(id: FloorType): string {
  return FLOOR_TYPES.find((t) => t.id === id)?.displayName ?? id;
}

export interface Floor {
  id: string;
  projectId: string;
  name: string;
  floorType: FloorType;
  sortOrder: number;
  notes: string;
  /** Phase 7: default ceiling/wall height for the floor plan (null = 96"). */
  defaultCeilingHeightInches: number | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FloorInput {
  name: string;
  floorType: FloorType;
  notes: string;
}

/**
 * The name a new floor gets when the user has not typed one: Floor 1, then
 * Floor 2, Floor 3… skipping any number already taken, so adding a floor to
 * a plan that already has "Floor 1" and "Floor 2" suggests "Floor 3" rather
 * than colliding. Renaming is always the user's call afterwards.
 */
export function nextDefaultFloorName(existingNames: readonly string[]): string {
  const taken = new Set(
    existingNames
      .map((name) => /^floor\s+(\d+)$/i.exec(name.trim()))
      .filter((match): match is RegExpExecArray => match !== null)
      .map((match) => Number.parseInt(match[1], 10)),
  );
  let n = 1;
  while (taken.has(n)) n += 1;
  return `Floor ${n}`;
}

export function validateFloorInput(input: FloorInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") {
    problems.push("Floor name is required.");
  }
  return problems;
}
